import { forwardRef } from "react";
import { Check, CheckCheck, AlertCircle, Clock } from "lucide-react";
import { cn } from "@/lib/cn";

/**
 * MessageStatusIcon — Telegram-uslub xabar yetkazib berish ko'rsatkichi.
 *
 * Token-aware (--message-status-*).
 *
 * Holatlar:
 *   PENDING   — clock (jo'natilmoqda)        muted
 *   SENT      — bitta tick                    secondary
 *   DELIVERED — ikki tick (kulrang)           primary
 *   READ      — ikki tick cyan (o'qildi)      cyan/accent
 *   FAILED    — alert (xato)                  danger
 *
 * Foydalanish:
 *   <MessageStatusIcon status="READ" />
 *   <MessageStatusIcon status="SENT" size="sm" />
 */

export type MessageStatus =
  | "PENDING"
  | "SENT"
  | "DELIVERED"
  | "READ"
  | "FAILED";

export interface MessageStatusIconProps
  extends Omit<React.HTMLAttributes<HTMLSpanElement>, "children"> {
  status: MessageStatus | string | null | undefined;
  size?: "xs" | "sm" | "md";
  /** Inherit qiluvchi rang (parent bubble'da) — true bo'lsa token o'rniga `currentColor` */
  inheritColor?: boolean;
}

const SIZE_CLS: Record<NonNullable<MessageStatusIconProps["size"]>, string> = {
  xs: "h-3 w-3",
  sm: "h-3.5 w-3.5",
  md: "h-4 w-4",
};

const LABEL: Record<MessageStatus, string> = {
  PENDING: "Jo'natilmoqda",
  SENT: "Yuborildi",
  DELIVERED: "Yetkazildi",
  READ: "O'qildi",
  FAILED: "Xato — qayta urinish",
};

const COLOR_VAR: Record<MessageStatus, string> = {
  PENDING: "var(--message-status-pending)",
  SENT: "var(--message-status-sent)",
  DELIVERED: "var(--message-status-delivered)",
  READ: "var(--message-status-read)",
  FAILED: "var(--message-status-failed)",
};

export const MessageStatusIcon = forwardRef<
  HTMLSpanElement,
  MessageStatusIconProps
>(({ status, size = "sm", inheritColor, className, style, ...props }, ref) => {
  const key = (status ?? "SENT").toString().toUpperCase() as MessageStatus;
  const safe: MessageStatus = (
    ["PENDING", "SENT", "DELIVERED", "READ", "FAILED"] as MessageStatus[]
  ).includes(key)
    ? key
    : "SENT";

  const Icon =
    safe === "PENDING"
      ? Clock
      : safe === "SENT"
        ? Check
        : safe === "FAILED"
          ? AlertCircle
          : CheckCheck;

  return (
    <span
      ref={ref}
      role="status"
      aria-label={LABEL[safe]}
      title={LABEL[safe]}
      className={cn("inline-flex items-center", className)}
      style={{
        color: inheritColor ? "currentColor" : COLOR_VAR[safe],
        ...style,
      }}
      {...props}
    >
      <Icon className={SIZE_CLS[size]} aria-hidden />
    </span>
  );
});
MessageStatusIcon.displayName = "MessageStatusIcon";
